// Pure helpers for the customer primary contact / address pickers.
// Location primaries are per LocationType: a customer has one primary delivery
// address and one primary postal address.

export interface PickerContact {
  ID: string;
  FirstName?: string | null;
  LastName?: string | null;
  FullName?: string | null;
  EmailAddress?: string | null;
  MobileNumber?: string | null;
  IsPrimary?: boolean;
  IsPrimaryAccount?: boolean;
  IsActive?: boolean;
  [key: string]: any;
}

export interface PickerLocation {
  ID: string;
  Description?: string | null;
  LocationDisplay?: string | null;
  LocationType: number;
  IsPrimary?: boolean;
  IsActive?: boolean;
  [key: string]: any;
}

export interface PickerCustomer {
  Contacts?: PickerContact[] | null;
  Locations?: PickerLocation[] | null;
  [key: string]: any;
}

const isActive = (x: { IsActive?: boolean }) => x.IsActive !== false;
const sameType = (a: PickerLocation, b: { LocationType: number }) => Number(a.LocationType) === Number(b.LocationType);

export const contactName = (c: PickerContact): string =>
  c.FullName?.trim() || [c.FirstName, c.LastName].filter(Boolean).join(' ').trim();

export const locationName = (l: PickerLocation): string =>
  l.Description?.trim() || l.LocationDisplay?.trim() || 'This address';

export const activeContactsOf = (customer: PickerCustomer): PickerContact[] =>
  (customer.Contacts ?? []).filter(isActive);

export const primaryContactOf = (customer: PickerCustomer): PickerContact | undefined =>
  activeContactsOf(customer).find(c => c.IsPrimary);

export const activeLocationsOf = (customer: PickerCustomer, type: number): PickerLocation[] =>
  (customer.Locations ?? []).filter(l => isActive(l) && sameType(l, { LocationType: type }));

export const primaryLocationOf = (customer: PickerCustomer, type: number): PickerLocation | undefined =>
  activeLocationsOf(customer, type).find(l => l.IsPrimary);

export function setPrimaryContact(customer: PickerCustomer, contact: Pick<PickerContact, 'ID'>): PickerCustomer {
  return {
    ...customer,
    Contacts: (customer.Contacts ?? []).map(c => ({ ...c, IsPrimary: c.ID === contact.ID })),
  };
}

export function setPrimaryLocation(customer: PickerCustomer, location: Pick<PickerLocation, 'ID' | 'LocationType'>): PickerCustomer {
  return {
    ...customer,
    Locations: (customer.Locations ?? []).map(l =>
      sameType(l, location) ? { ...l, IsPrimary: l.ID === location.ID } : l),
  };
}

export function upsertContact(customer: PickerCustomer, contact: PickerContact): PickerCustomer {
  // One primary and one accounting contact: the saved contact takes the role from the others, as the API does
  const cleared = (customer.Contacts ?? []).map(c => ({
    ...c,
    ...(contact.IsPrimary && { IsPrimary: false }),
    ...(contact.IsPrimaryAccount && { IsPrimaryAccount: false }),
  }));
  const found = cleared.some(c => c.ID === contact.ID);
  return {
    ...customer,
    Contacts: found ? cleared.map(c => (c.ID === contact.ID ? contact : c)) : [...cleared, contact],
  };
}

export function upsertLocation(customer: PickerCustomer, location: PickerLocation): PickerCustomer {
  const existing = customer.Locations ?? [];
  const cleared = location.IsPrimary
    ? existing.map(l => (sameType(l, location) ? { ...l, IsPrimary: false } : l))
    : existing;
  const found = cleared.some(l => l.ID === location.ID);
  return {
    ...customer,
    // New locations go first, as the previous selector did.
    Locations: found ? cleared.map(l => (l.ID === location.ID ? location : l)) : [location, ...cleared],
  };
}

// After a contact dialog save: merges the saved contact and the other active contacts as reloaded
// from the API (for their RowVersions). The reload carries the server's primary, so a primary picked
// on the page but not yet saved is picked again, unless the saved contact took or gave up the role itself.
export function mergeSavedContact(customer: PickerCustomer, saved: PickerContact, related: PickerContact[] = []): PickerCustomer {
  const pending = primaryContactOf(customer);
  const merged = [saved, ...related].reduce(upsertContact, customer);
  const keepPending = pending && pending.ID !== saved.ID && !saved.IsPrimary
    && activeContactsOf(merged).some(c => c.ID === pending.ID);
  return keepPending ? setPrimaryContact(merged, pending) : merged;
}

// Same as mergeSavedContact, per location type
export function mergeSavedLocation(customer: PickerCustomer, saved: PickerLocation, related: PickerLocation[] = []): PickerCustomer {
  const types = [...new Set((customer.Locations ?? []).map(l => Number(l.LocationType)))];
  const pending = types.map(t => primaryLocationOf(customer, t)).filter((l): l is PickerLocation => !!l);
  let merged = [saved, ...related].reduce(upsertLocation, customer);
  for (const p of pending) {
    const savedTookRole = !!saved.IsPrimary && sameType(saved, p);
    if (p.ID !== saved.ID && !savedTookRole && activeLocationsOf(merged, p.LocationType).some(l => l.ID === p.ID)) {
      merged = setPrimaryLocation(merged, p);
    }
  }
  return merged;
}

// Same conversion as contact-selector.tsx: digits only, local 0 prefix becomes 27.
export function whatsAppUrl(mobile: string): string {
  let phone = mobile.replace(/\D/g, '');
  if (phone.startsWith('0')) {
    phone = '27' + phone.substring(1);
  }
  return `https://wa.me/${phone}?text=`;
}
